import React from "react";

/* Sparkles — a dependency-free take on the Aceternity sparkles effect
   (https://ui.aceternity.com/components/sparkles). Draws twinkling
   particles onto a <canvas> that fills its parent. No tsparticles / motion
   needed, so it slots into our plain-CSS, no-Tailwind setup.

   Props:
     background    – canvas fill (default transparent)
     particleColor – dot color, any CSS color (default "#FFFFFF")
     minSize/maxSize – particle radius range in px
     density       – approx particles per 10,000px² of canvas
     speed         – twinkle speed multiplier

   For drop-in compatibility with Aceternity's <SparklesCore />, it also
   accepts particleDensity (tsparticles' count knob) and maps it onto our
   per-area density. */
export default function Sparkles({
  background = "transparent",
  particleColor = "#FFFFFF",
  minSize = 0.6,
  maxSize = 1.6,
  density = .1,
  particleDensity,
  speed = 1,
  className = "",
  style,
}) {
  // Aceternity's particleDensity (~hundreds–thousands) → our per-10,000px²
  // density (~1–2). 800 keeps a dense field readable without overdrawing.
  const effectiveDensity =
    particleDensity != null ? particleDensity / 800 : density;
  const canvasRef = React.useRef(null);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    let particles = [];
    let raf = 0;
    let dpr = Math.min(window.devicePixelRatio || 1, 2);

    // Canvas fillStyle can't read CSS custom properties, so resolve
    // "var(--x)" against the canvas element's computed styles.
    const resolveColor = (c) => {
      const m = /^var\((--[^,)]+)(?:,\s*([^)]+))?\)$/.exec(c.trim());
      if (!m) return c;
      const v = getComputedStyle(canvas).getPropertyValue(m[1]).trim();
      return v || (m[2] ? m[2].trim() : "#FFFFFF");
    };
    const fill = resolveColor(particleColor);

    const seed = () => {
      const { width, height } = canvas.getBoundingClientRect();
      canvas.width = Math.max(1, Math.floor(width * dpr));
      canvas.height = Math.max(1, Math.floor(height * dpr));
      const count = Math.round((width * height) / 10000 * effectiveDensity);
      particles = Array.from({ length: count }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        r: minSize + Math.random() * (maxSize - minSize),
        // twinkle phase + rate
        a: Math.random(),
        rate: (0.005 + Math.random() * 0.02) * speed,
        dir: Math.random() > 0.5 ? 1 : -1,
      }));
    };

    const draw = () => {
      const { width, height } = canvas.getBoundingClientRect();
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, width, height);
      if (background !== "transparent") {
        ctx.fillStyle = background;
        ctx.fillRect(0, 0, width, height);
      }
      ctx.fillStyle = fill;
      for (const p of particles) {
        if (!reduceMotion) {
          p.a += p.rate * p.dir;
          if (p.a <= 0.05) { p.a = 0.05; p.dir = 1; }
          if (p.a >= 1) { p.a = 1; p.dir = -1; }
        }
        ctx.globalAlpha = reduceMotion ? 0.7 : p.a;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      raf = window.requestAnimationFrame(draw);
    };

    seed();
    draw();

    const ro = new ResizeObserver(() => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      seed();
    });
    ro.observe(canvas);

    return () => {
      window.cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [background, particleColor, minSize, maxSize, effectiveDensity, speed]);

  return (
    <canvas
      ref={canvasRef}
      className={"sparkles-canvas " + className}
      style={style}
      aria-hidden="true"
    />
  );
}

// Drop-in alias matching Aceternity's import name.
export const SparklesCore = Sparkles;
